/**
 * iter76 — Admin → Categories page.
 * Full CRUD:
 *   • Add new category
 *   • Rename (cascades to products.category via backend)
 *   • Soft-delete (hides from storefront but keeps historical linkage)
 *   • Restore soft-deleted categories
 *   • Reorder via up/down arrows — persisted to db.categories.order
 * Every consumer (Shop filter, SearchOverlay, admin Product Editor,
 * combo editor, homepage grid) reads /api/categories, so a save here
 * propagates instantly with no redeploy.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, ArrowUp, ArrowDown, RotateCcw, Save, Search } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  // iter81 — SEO edit modal state.
  const [seoCat, setSeoCat] = useState(null);
  const [seoDraft, setSeoDraft] = useState({ seo_title: "", seo_description: "", og_image: "" });
  const [seoSaving, setSeoSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/categories");
      setCats(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load categories"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await api.post("/admin/categories", { name: newName.trim() });
      setNewName("");
      toast.success("Category added.");
      load();
    } catch (e) { toast.error(formatApiError(e, "Add failed")); }
  };

  const startEdit = (c) => { setEditId(c.id); setEditName(c.name); };
  const saveEdit = async (id) => {
    if (!editName.trim()) return;
    try {
      await api.put(`/admin/categories/${id}`, { name: editName.trim() });
      toast.success("Renamed. Existing products also updated.");
      setEditId(null);
      load();
    } catch (e) { toast.error(formatApiError(e, "Rename failed")); }
  };

  const toggleDeleted = async (c) => {
    const nowDeleted = !c.is_deleted;
    if (nowDeleted && !window.confirm(`Soft-delete "${c.name}"? It will be hidden from the storefront but existing products stay linked and you can restore later.`)) return;
    try {
      await api.put(`/admin/categories/${c.id}`, { is_deleted: nowDeleted });
      toast.success(nowDeleted ? "Category hidden." : "Category restored.");
      load();
    } catch (e) { toast.error(formatApiError(e, "Update failed")); }
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= cats.length) return;
    const copy = [...cats];
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    // reassign contiguous 0..N order.
    setCats(copy.map((c, i) => ({ ...c, order: i })));
  };
  const saveOrder = async () => {
    setSaving(true);
    try {
      const payload = cats.map((c, i) => ({ id: c.id, order: i }));
      await api.post("/admin/categories/reorder", payload);
      toast.success("Order saved.");
      load();
    } catch (e) { toast.error(formatApiError(e, "Reorder failed")); }
    finally { setSaving(false); }
  };

  // iter81 — SEO editor
  const openSeo = (c) => {
    setSeoCat(c);
    setSeoDraft({
      seo_title: c.seo_title || "",
      seo_description: c.seo_description || "",
      og_image: c.og_image || "",
      google_product_category: c.google_product_category || "",
    });
  };
  const closeSeo = () => { if (!seoSaving) setSeoCat(null); };
  const saveSeo = async () => {
    if (!seoCat) return;
    setSeoSaving(true);
    try {
      await api.put(`/admin/categories/${seoCat.id}`, seoDraft);
      toast.success("SEO metadata saved.");
      setSeoCat(null);
      load();
    } catch (e) { toast.error(formatApiError(e, "Save failed")); }
    finally { setSeoSaving(false); }
  };

  return (
    <div className="space-y-8" data-testid="admin-categories-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-4xl text-brand-dark">Categories <span className="text-base text-brand-muted ml-2">({cats.filter(c => !c.is_deleted).length} active · {cats.filter(c => c.is_deleted).length} hidden)</span></h1>
        <button onClick={saveOrder} disabled={saving} className="btn-primary text-sm disabled:opacity-60" data-testid="categories-save-order">
          <Save className="w-4 h-4 mr-1" />{saving ? "Saving…" : "Save order"}
        </button>
      </div>

      <form onSubmit={add} className="flex items-center gap-2">
        <input
          className="input-field flex-1"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="e.g. Snacks, Sweets, Chutneys"
          data-testid="categories-new-name"
        />
        <button className="btn-primary" data-testid="categories-add-btn"><Plus className="w-4 h-4 mr-1" />Add category</button>
      </form>

      <div className="bg-white rounded-2xl border border-brand-line divide-y divide-brand-line">
        {loading && <div className="p-6 text-brand-muted text-sm">Loading…</div>}
        {!loading && cats.length === 0 && <div className="p-6 text-brand-muted text-sm">No categories yet. Add one above.</div>}
        {cats.map((c, i) => (
          <div key={c.id} className={`flex items-center gap-3 p-4 ${c.is_deleted ? "opacity-50" : ""}`} data-testid={`category-row-${c.id}`}>
            <span className="text-xs text-brand-muted w-8">#{i + 1}</span>
            {editId === c.id ? (
              <input
                autoFocus
                className="input-field flex-1"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onBlur={() => saveEdit(c.id)}
                onKeyDown={(e) => { if (e.key === "Enter") saveEdit(c.id); if (e.key === "Escape") setEditId(null); }}
                data-testid={`category-name-edit-${c.id}`}
              />
            ) : (
              <button className="flex-1 text-left text-brand-dark font-medium hover:text-brand-chili" onClick={() => startEdit(c)} data-testid={`category-name-${c.id}`}>
                {c.name}{c.is_deleted && <span className="ml-2 text-xs text-brand-muted italic">(hidden)</span>}
              </button>
            )}
            <div className="flex items-center gap-1">
              <button onClick={() => openSeo(c)} className="p-1.5 text-brand-muted hover:text-brand-dark" title="Edit SEO" data-testid={`category-seo-${c.id}`}>
                <Search className="w-4 h-4" />
              </button>
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 text-brand-muted hover:text-brand-dark disabled:opacity-30" data-testid={`category-up-${c.id}`}><ArrowUp className="w-4 h-4" /></button>
              <button onClick={() => move(i, +1)} disabled={i === cats.length - 1} className="p-1.5 text-brand-muted hover:text-brand-dark disabled:opacity-30" data-testid={`category-down-${c.id}`}><ArrowDown className="w-4 h-4" /></button>
              <button onClick={() => toggleDeleted(c)} className="p-1.5 text-brand-muted hover:text-brand-chili" data-testid={`category-toggle-${c.id}`} title={c.is_deleted ? "Restore" : "Soft-delete"}>
                {c.is_deleted ? <RotateCcw className="w-4 h-4" /> : <Trash className="w-4 h-4" />}
              </button>
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-brand-muted">
        Soft-deleted categories are hidden from customers but stay linked to existing products in the database. Restore any time. Reorder using the arrows and click <b>Save order</b> — the new order propagates instantly to the homepage grid, shop filters, search, and product editors.
      </p>

      {/* iter81 — SEO edit modal */}
      {seoCat && (
        <div
          className="fixed inset-0 z-[200] bg-brand-dark/55 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) closeSeo(); }}
          data-testid="category-seo-modal"
        >
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="font-serif text-2xl text-brand-dark">SEO — {seoCat.name}</h3>
            <p className="text-xs text-brand-muted -mt-2">
              Overrides the auto-generated title / description for <b>/{seoCat.name.toLowerCase().replace(/\s+/g,"-")}</b>. Leave blank to fall back to <em>&ldquo;{seoCat.name} | Sri Durga Foods&rdquo;</em>.
            </p>
            <div>
              <label className="label-field">SEO Title <span className="text-brand-muted font-normal">(60 chars max)</span></label>
              <input
                className="input-field"
                maxLength={70}
                value={seoDraft.seo_title}
                onChange={(e) => setSeoDraft({ ...seoDraft, seo_title: e.target.value })}
                placeholder={`${seoCat.name} | Sri Durga Foods`}
                data-testid="category-seo-title-input"
              />
            </div>
            <div>
              <label className="label-field">Meta Description <span className="text-brand-muted font-normal">(160 chars max)</span></label>
              <textarea
                className="input-field min-h-[80px]"
                maxLength={180}
                value={seoDraft.seo_description}
                onChange={(e) => setSeoDraft({ ...seoDraft, seo_description: e.target.value })}
                placeholder={`Shop authentic ${seoCat.name} from Sri Durga Foods — handcrafted in Nellore.`}
                data-testid="category-seo-description-input"
              />
            </div>
            <div>
              <label className="label-field">OpenGraph image URL <span className="text-brand-muted font-normal">(optional)</span></label>
              <input
                className="input-field"
                value={seoDraft.og_image}
                onChange={(e) => setSeoDraft({ ...seoDraft, og_image: e.target.value })}
                placeholder="/icon-512.png or full https:// URL"
                data-testid="category-seo-og-input"
              />
            </div>
            <div>
              <label className="label-field">Google Product Category <span className="text-brand-muted font-normal">(GMC feed override)</span></label>
              <input
                className="input-field"
                value={seoDraft.google_product_category}
                onChange={(e) => setSeoDraft({ ...seoDraft, google_product_category: e.target.value })}
                placeholder="e.g. Food, Beverages & Tobacco > Food Items > Seasonings & Spices"
                data-testid="category-gmc-input"
              />
              <p className="text-[11px] text-brand-muted mt-1">Leave blank to auto-map from Sri Durga&rsquo;s built-in taxonomy defaults.</p>
            </div>
            <div className="flex items-center justify-end gap-3 pt-2">
              <button type="button" onClick={closeSeo} disabled={seoSaving} className="btn-secondary" data-testid="category-seo-cancel">Cancel</button>
              <button type="button" onClick={saveSeo} disabled={seoSaving} className="btn-primary" data-testid="category-seo-save">
                {seoSaving ? "Saving…" : "Save SEO"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
